/**
 * ActionItems — Section 2 of the Office Manager Report.
 *
 * Displays prioritised action items grouped by severity.
 * data-testid: candeza-reports-operations-actions-*
 */

import React from 'react';
import styled from 'styled-components';
import type { ActionItemsSection, ActionItem, Severity } from '@/api/reports';
import { SEVERITY_COLORS, SEVERITY_ICONS } from '@/api/reports';

// ── Styled components ─────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ItemCard = styled.div<{ $severity: Severity }>`
  border: 1px solid ${({ $severity }) => SEVERITY_COLORS[$severity]}44;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const ItemHeader = styled.div<{ $severity: Severity }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ $severity }) => SEVERITY_COLORS[$severity]}18;
`;

const ItemTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const Count = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
`;

const ItemBody = styled.div`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.surface};
`;

// Prompt 11E.8: no horizontal-scroll wrapper meant these 4-column tables
// compressed and overlapped at narrow widths — same fix idiom as
// TodaysSchedule.tsx's TableScroll.
const SmallTableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const SmallTable = styled.table`
  width: 100%;
  min-width: 420px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  border-collapse: collapse;

  th {
    text-align: left;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    padding: 4px 6px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  td {
    padding: 4px 6px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};

    &:last-child { border-bottom: none; }
  }

  tr:last-child td { border-bottom: none; }
`;

const EmptyNote = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-style: italic;
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function humaniseType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Sub-renderers ─────────────────────────────────────────────────────────────

function OverdueInvoiceItems({ items }: { items: unknown[] }) {
  const rows = items as Array<{
    student_name: string;
    invoice_number: string;
    days_overdue: number;
    amount_due: number;
  }>;
  if (!rows.length) return <EmptyNote>No overdue invoices.</EmptyNote>;
  return (
    <SmallTableWrap>
      <SmallTable>
        <thead>
          <tr>
            <th>Student</th>
            <th>Invoice</th>
            <th>Days Overdue</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((r, i) => (
            <tr key={i}>
              <td>{r.student_name}</td>
              <td>{r.invoice_number}</td>
              <td>{r.days_overdue}d</td>
              <td>₹{r.amount_due.toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </SmallTable>
    </SmallTableWrap>
  );
}

function ExpiringEntitlementItems({ items }: { items: unknown[] }) {
  const rows = items as Array<{
    student_name: string;
    package_name: string;
    sessions_remaining: number;
    expires_at: string;
  }>;
  if (!rows.length) return <EmptyNote>No expiring entitlements.</EmptyNote>;
  return (
    <SmallTableWrap>
      <SmallTable>
        <thead>
          <tr><th>Student</th><th>Package</th><th>Remaining</th><th>Expires</th></tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((r, i) => (
            <tr key={i}>
              <td>{r.student_name}</td>
              <td>{r.package_name}</td>
              <td>{r.sessions_remaining}</td>
              <td>{r.expires_at}</td>
            </tr>
          ))}
        </tbody>
      </SmallTable>
    </SmallTableWrap>
  );
}

function TrialUnconfirmedItems({ items }: { items: unknown[] }) {
  const rows = items as Array<{
    contact_name: string;
    requested_subject: string | null;
    hours_waiting: number;
  }>;
  if (!rows.length) return <EmptyNote>No unconfirmed trial bookings.</EmptyNote>;
  return (
    <SmallTableWrap>
      <SmallTable>
        <thead>
          <tr><th>Contact</th><th>Subject</th><th>Waiting</th></tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((r, i) => (
            <tr key={i}>
              <td>{r.contact_name}</td>
              <td>{r.requested_subject ?? '—'}</td>
              <td>{Math.round(r.hours_waiting)}h</td>
            </tr>
          ))}
        </tbody>
      </SmallTable>
    </SmallTableWrap>
  );
}

function GenericItems({ count }: { count: number }) {
  return <EmptyNote>{count} item(s) require attention.</EmptyNote>;
}

function renderItems(item: ActionItem) {
  if (item.count === 0) return <EmptyNote>All clear ✓</EmptyNote>;
  switch (item.type) {
    case 'overdue_invoices': return <OverdueInvoiceItems items={item.items} />;
    case 'expiring_entitlements': return <ExpiringEntitlementItems items={item.items} />;
    case 'trial_unconfirmed': return <TrialUnconfirmedItems items={item.items} />;
    default: return <GenericItems count={item.count} />;
  }
}

// ── Order by severity ─────────────────────────────────────────────────────────

const SEV_ORDER: Record<Severity, number> = {
  critical: 0, high: 1, medium: 2, low: 3,
};

function orderedItems(section: ActionItemsSection): ActionItem[] {
  const all: ActionItem[] = [
    section.overdue_invoices,
    section.expiring_entitlements,
    section.attendance_pending,
    section.trial_unconfirmed,
    section.low_balance_students,
  ];
  return [...all].sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ActionItemsProps {
  section: ActionItemsSection;
  loading?: boolean;
}

export const ActionItems: React.FC<ActionItemsProps> = ({ section, loading }) => {
  if (loading) {
    return <div data-testid="candeza-reports-operations-actions-loading">Loading action items…</div>;
  }

  const items = orderedItems(section);
  const hasAny = items.some((i) => i.count > 0);

  if (!hasAny) {
    return (
      <div data-testid="candeza-reports-operations-actions-empty" style={{ color: '#10b981', fontWeight: 600 }}>
        ✓ No action items — everything is on track!
      </div>
    );
  }

  return (
    <Container data-testid="candeza-reports-operations-actions-list">
      {items.map((item) => (
        <ItemCard
          key={item.type}
          $severity={item.severity}
          data-testid={`candeza-reports-operations-action-${item.type}`}
        >
          <ItemHeader $severity={item.severity}>
            <ItemTitle>
              <span>{SEVERITY_ICONS[item.severity]}</span>
              <span>{humaniseType(item.type)}</span>
            </ItemTitle>
            <Count style={{ color: SEVERITY_COLORS[item.severity] }}>{item.count}</Count>
          </ItemHeader>
          {item.count > 0 && <ItemBody>{renderItems(item)}</ItemBody>}
        </ItemCard>
      ))}
    </Container>
  );
};

